// File: frontend/src/components/admin/AdminPeople.tsx
// src/components/admin/AdminPeople.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaSearch, FaFilter, FaTimes, FaPlus, FaEdit, FaTrash,
  FaSort, FaSortUp, FaSortDown, FaToggleOn, FaToggleOff, FaLock, FaUnlock, FaInfoCircle,
} from "react-icons/fa";

import { PersonModel, CareerEntryModel } from "../../models/PersonModel";
import { ClubModel } from "../../models/ClubModel";
import {
  AdminPersonFilters,
  getAdminPeople,
  getAdminPersonById,
  setPersonLocked,
} from "../../services/adminPersonService";
import clubService from "../../services/clubService";
import apiClient from "../../services/apiClient";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import { ConfirmModal } from "../common/ConfirmModal";
import { LockReasonFields } from "./common/LockReasonFields";
import { LockInfoModal } from "./common/LockInfoModal";
import { SearchableSelect, SearchableSelectOption } from "./common/SearchableSelect";
import {
  ADMIN_PAGE_SIZE,
  ADMIN_PERSON_NAME_MAX_LENGTH,
  ADMIN_PERSON_BIRTH_CITY_MAX_LENGTH,
  ADMIN_PERSON_BIO_MAX_LENGTH,
  ADMIN_CAREER_NOTES_MAX_LENGTH,
  ADMIN_CAREER_CLUB_NAME_MAX_LENGTH,
  ADMIN_URL_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { InputClearButton } from "../common/InputClearButton";
import { AdminCharCounter } from "./common";
import { MdCountryDto, getPublicCountries } from "../../services/masterDataService";
import "./AdminPeople.css";
import { usePersonRoles } from "../../hooks/usePersonRoles";

// All SportType values — must match backend enum
const SPORT_TYPES = [
  "FOOTBALL", "BASKETBALL", "VOLLEYBALL",
  "AMERICAN_FOOTBALL", "BASEBALL", "ICE_HOCKEY", "HANDBALL",
];

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const CURRENT_YEAR = new Date().getFullYear();

interface AdminPeopleProps {
  isAdmin: boolean;
}

// ── helpers ───────────────────────────────────────────────────────────────

const getInitials = (firstName: string, lastName: string) =>
  `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;

/* UK home nations use pseudo ISO codes that have no real alpha-2 flag file,
   so they are mapped explicitly before falling back to the alpha-2 slice. */
const UK_NATION_FLAG_CODES: Record<string, string> = {
  ENG: "gb-eng",
  SCO: "gb-sct",
  WAL: "gb-wls",
  NIR: "gb-nir",
};

/** Resolve a country code to its flag SVG served from the shared media folder. */
const flagUrl = (code: string | null | undefined): string | null => {
  if (!code) return null;
  const upper = code.toUpperCase();
  const flagCode = UK_NATION_FLAG_CODES[upper] ?? code.slice(0, 2).toLowerCase();
  return `/media/flags/4x3/${flagCode}.svg`;
};

/** Format an ISO date string as dd.MM.yyyy — returns null when unset/invalid. */
const formatBirthDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const formatYears = (start: number, end: number | null) =>
  end ? `${start}–${end}` : `${start}–güncel`;

/**
 * Pick the person's most recent career stint — the club they last played for,
 * the TV channel they currently work at, and so on.
 *
 * Ordering rules, applied in this order:
 *  1. Ongoing stints (endYear == null) always win over finished ones.
 *  2. Later end year wins.
 *  3. Later start year wins (tie-breaker for stints ending in the same year).
 *  4. Higher entry id wins (stable, deterministic result for full ties).
 *
 * Returns null when the person has no career history at all.
 */
const getLatestCareerEntry = (
  entries: CareerEntryModel[] | null | undefined
): CareerEntryModel | null => {
  if (!entries || entries.length === 0) return null;

  return entries.reduce((best, current) => {
    const bestOngoing    = best.endYear == null ? 1 : 0;
    const currentOngoing = current.endYear == null ? 1 : 0;
    if (currentOngoing !== bestOngoing) return currentOngoing > bestOngoing ? current : best;

    const bestEnd    = best.endYear ?? 0;
    const currentEnd = current.endYear ?? 0;
    if (currentEnd !== bestEnd) return currentEnd > bestEnd ? current : best;

    const bestStart    = best.startYear ?? 0;
    const currentStart = current.startYear ?? 0;
    if (currentStart !== bestStart) return currentStart > bestStart ? current : best;

    return current.id > best.id ? current : best;
  });
};

/**
 * Club name, or the free-text organization name for non-club roles.
 * Entries that carry no affiliation at all (e.g. a referee without a federation)
 * fall back to the caller-supplied label — usually the translated role — instead
 * of showing a bare dash.
 */
const careerAffiliationLabel = (entry: CareerEntryModel, fallback: string): string =>
  entry.clubShortName ?? entry.customClubName ?? fallback;

// ── component ─────────────────────────────────────────────────────────────

export const AdminPeople: React.FC<AdminPeopleProps> = ({ isAdmin }) => {
  const { t } = useTranslation();
  const { roles: personRoles, translateRole, getAffiliationType } = usePersonRoles();

  // Countries from master data
  const [countries, setCountries] = useState<MdCountryDto[]>([]);
  useEffect(() => {
    getPublicCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  // ── Data ──────────────────────────────────────────────────────
  const [people, setPeople]   = useState<PersonModel[]>([]);

  // ── Lock modal state (shown only when locking, not unlocking) ──
  const [lockTarget, setLockTarget] = useState<PersonModel | null>(null);
  const [lockReasonDraft, setLockReasonDraft] = useState("");
  const [lockReasonVisibleDraft, setLockReasonVisibleDraft] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockInfoTarget, setLockInfoTarget] = useState<PersonModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalItems, setTotalItems]   = useState(0);

  // Filters
  const [filters, setFilters] = useState<AdminPersonFilters>({
    search: "", nationality: "", birthCountry: "", active: "", clubId: "", currentClubId: "", locked: "", sortBy: "lastName", sortDir: "asc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // Full club list used by the two club filter dropdowns (career club / current club)
  const [allClubs, setAllClubs] = useState<ClubModel[]>([]);

  // ── Modal state ───────────────────────────────────────────────
  type ModalType = "create" | "edit" | "delete" | "career-add" | "career-edit" | "career-delete" | null;
  const [modalType, setModalType]           = useState<ModalType>(null);
  const [selectedPerson, setSelectedPerson] = useState<PersonModel | null>(null);
  const [selectedEntry, setSelectedEntry]   = useState<CareerEntryModel | null>(null);
  const [modalError, setModalError]         = useState<string | null>(null);

  // ── Person form ───────────────────────────────────────────────
  const emptyPersonForm = {
    firstName: "", lastName: "", knownName: "", birthDate: "", birthCity: "",
    birthCountryCode: "TR", nationality: "TR", photoUrl: "", bio: "", active: true,
  };
  const [personForm, setPersonForm] = useState({ ...emptyPersonForm });

  // ── Career entry form ─────────────────────────────────────────
  // endYear is intentionally empty by default: an empty value means "still ongoing".
  // It used to be pre-filled with the current year, which made an open-ended entry look
  // like it ended this year when the record was reopened.
  const emptyCareerForm = {
    role: "PLAYER",
    sportType: "FOOTBALL",
    startYear: CURRENT_YEAR,
    endYear: "" as string | number,
    notes: "",
  };
  const [careerForm, setCareerForm] = useState({ ...emptyCareerForm });

  // Affiliation kind of the currently selected role — drives the label, the input type
  // and whether the field is required at all.
  const careerAffiliation = getAffiliationType(careerForm.role);
  const needsClubSearch   = careerAffiliation === "CLUB";
  // The affiliation field is always rendered — only the requirement changes. NONE roles
  // (referees) have no club, but they can still work for a federation such as TFF, and
  // that name must survive the save instead of being dropped.
  const affiliationRequired = careerAffiliation !== "NONE";

  // An empty end year means the stint is still ongoing
  const careerOngoing = careerForm.endYear === "";

  // Career modal club state — single input: if user selects from dropdown, clubId is set;
  // if they just type freely, the text becomes customClubName on submit.
  const [clubQuery, setClubQuery]                   = useState("");
  const [clubResults, setClubResults]               = useState<ClubModel[]>([]);
  const [clubSearchLoading, setClubSearchLoading]   = useState(false);
  const [selectedClub, setSelectedClub]             = useState<ClubModel | null>(null);
  const [showClubDrop, setShowClubDrop]             = useState(false);
  const clubDropRef = useRef<HTMLDivElement>(null);

  // ── Club search debounce for career modal ─────────────────────
  useEffect(() => {
    // Club search only makes sense for CLUB roles — organization names are free text
    if (!needsClubSearch) {
      setClubResults([]);
      setShowClubDrop(false);
      return;
    }
    if (clubQuery.trim().length < 2) {
      setClubResults([]);
      setShowClubDrop(false);
      return;
    }
    const timer = setTimeout(async () => {
      setClubSearchLoading(true);
      try {
        const results = await clubService.searchClubs(clubQuery);
        setClubResults(results);
        setShowClubDrop(results.length > 0);
      } catch {
        setClubResults([]);
      } finally {
        setClubSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clubQuery, needsClubSearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clubDropRef.current && !clubDropRef.current.contains(e.target as Node)) {
        setShowClubDrop(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Career modal: select club from dropdown ───────────────────
  const handleSelectClub = (club: ClubModel) => {
    setSelectedClub(club);
    setClubQuery(club.nameTr ?? club.shortName);
    setClubResults([]);
    setShowClubDrop(false);
  };

  // When the user edits the club input, clear the selected club
  const handleClubQueryChange = (value: string) => {
    setClubQuery(value);
    if (selectedClub) setSelectedClub(null); // typed freely → clear selection
  };

  // ── Fetch ─────────────────────────────────────────────────────
  const fetchPeople = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminPeople(currentPage, ADMIN_PAGE_SIZE, filters);
      setPeople(res.content);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch (err: any) {
      setError(err.message || t("admin_person_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, t]);

  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  // ── Toolbar ───────────────────────────────────────────────────
  const handleSearch = () => {
    setCurrentPage(0);
    setFilters(prev => ({ ...prev, search: searchInput }));
  };

  // Clear-X next to the search box: empties the field AND drops the applied search
  const handleClearSearch = () => {
    setSearchInput("");
    setCurrentPage(0);
    setFilters(prev => ({ ...prev, search: "" }));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  // Load the full club list once so both club filters can be plain dropdowns
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await clubService.getAllClubs(0, 1000, "shortName", "asc");
        if (!cancelled) setAllClubs(res.content ?? []);
      } catch {
        if (!cancelled) setAllClubs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Options for the searchable country / club filter dropdowns.
  // The empty value acts as the "all" entry.
  const countryOptions: SearchableSelectOption[] = [
    { value: "", label: t("admin_all") },
    ...countries.map(c => ({ value: c.code, label: `${c.code} — ${c.displayName}` })),
  ];

  const clubOptions: SearchableSelectOption[] = [
    { value: "", label: t("admin_all") },
    ...allClubs.map(club => ({
      value: String(club.id),
      label: club.nameTr ?? club.shortName,
      hint: club.country ?? undefined,
    })),
  ];

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(0);
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc"
      ? <FaSortUp className="sort-icon active" />
      : <FaSortDown className="sort-icon active" />;
  };

  const handleClearFilters = () => {
    setFilters({ search: "", nationality: "", birthCountry: "", active: "", clubId: "", currentClubId: "", locked: "", sortBy: "lastName", sortDir: "asc" });
    setSearchInput("");
    setCurrentPage(0);
  };

  const hasActiveFilters = !!filters.search || !!filters.nationality || !!filters.birthCountry || filters.active !== "" || filters.clubId !== "" || filters.currentClubId !== "" || filters.locked !== "";

  const handlePaginate = (page: number) => setCurrentPage(page - 1);

  // ── Modal helpers ─────────────────────────────────────────────
  const closeModal = () => {
    setModalType(null);
    setSelectedPerson(null);
    setSelectedEntry(null);
    setModalError(null);
  };

  const resetClubState = () => {
    setClubQuery("");
    setClubResults([]);
    setSelectedClub(null);
    setShowClubDrop(false);
  };

  const openCreateModal = () => {
    setPersonForm({ ...emptyPersonForm });
    setModalError(null);
    setModalType("create");
  };

  const openEditModal = (person: PersonModel) => {
    setSelectedPerson(person);
    setPersonForm({
      firstName:        person.firstName,
      lastName:         person.lastName,
      knownName:        person.knownName ?? "",
      birthDate:        person.birthDate ?? "",
      birthCity:        person.birthCity ?? "",
      birthCountryCode: person.birthCountryCode ?? "TR",
      nationality:      person.nationality ?? "TR",
      photoUrl:         person.photoUrl ?? "",
      bio:              person.bio ?? "",
      active:           person.active,
    });
    setModalError(null);
    setModalType("edit");
  };

  // ── Topic lock/unlock ──────────────────────────────────────────────────────
  const handleToggleLock = async (person: PersonModel) => {
    if (!person.isLocked) {
      // About to lock — collect an optional reason first
      setLockTarget(person);
      setLockReasonDraft("");
      setLockReasonVisibleDraft(false);
      return;
    }
    try {
      const updated = await setPersonLocked(person.id, false);
      setPeople(prev => prev.map(p => p.id === person.id ? updated : p));
      setSuccessMessage(t("admin_topic_unlocked"));
    } catch {
      setError(t("admin_action_failed"));
    }
  };

  const handleConfirmLock = async () => {
    if (!lockTarget) return;
    setLockBusy(true);
    try {
      const updated = await setPersonLocked(lockTarget.id, true, lockReasonDraft.trim() || undefined, lockReasonVisibleDraft);
      setPeople(prev => prev.map(p => p.id === lockTarget.id ? updated : p));
      setSuccessMessage(t("admin_topic_locked"));
      setLockTarget(null);
    } catch {
      setError(t("admin_action_failed"));
    } finally {
      setLockBusy(false);
    }
  };

  const handleCancelLock = () => {
    setLockTarget(null);
    setLockReasonDraft("");
    setLockReasonVisibleDraft(false);
  };

  const openDeleteModal = (person: PersonModel) => {
    setSelectedPerson(person);
    setModalError(null);
    setModalType("delete");
  };

  // Career entry modals — load fresh person data to have the full entry list
  const openCareerAddModal = async (person: PersonModel) => {
    setCareerForm({ ...emptyCareerForm });
    resetClubState();
    setModalError(null);
    try {
      const fresh = await getAdminPersonById(person.id);
      setSelectedPerson(fresh);
    } catch {
      setSelectedPerson(person);
    }
    setModalType("career-add");
  };

  const openCareerEditModal = (person: PersonModel, entry: CareerEntryModel) => {
    setSelectedPerson(person);
    setSelectedEntry(entry);
    setCareerForm({
      role:      entry.role,
      sportType: entry.sportType,
      startYear: entry.startYear,
      // null end year = ongoing — keep the field empty instead of showing the current year
      endYear:   entry.endYear ?? "",
      notes:     entry.notes ?? "",
    });
    // Pre-fill club input from existing entry
    if (entry.clubId) {
      const name = entry.clubShortName ?? `Kulüp #${entry.clubId}`;
      setClubQuery(name);
      // Reconstruct a minimal ClubModel so the selected club ID is tracked
      setSelectedClub({ id: entry.clubId, shortName: name } as ClubModel);
    } else {
      setClubQuery(entry.customClubName ?? "");
      setSelectedClub(null);
    }
    setClubResults([]);
    setShowClubDrop(false);
    setModalError(null);
    setModalType("career-edit");
  };

  const openCareerDeleteModal = (person: PersonModel, entry: CareerEntryModel) => {
    setSelectedPerson(person);
    setSelectedEntry(entry);
    setModalError(null);
    setModalType("career-delete");
  };

  // ── CRUD: Person ──────────────────────────────────────────────
  const handleCreatePerson = async () => {
    if (!personForm.firstName.trim() || !personForm.lastName.trim()) {
      setModalError(t("admin_person_name_required"));
      return;
    }
    try {
      await apiClient.post("/api/admin/people", {
        firstName:        personForm.firstName.trim(),
        lastName:         personForm.lastName.trim(),
        knownName:        personForm.knownName.trim() || null,
        birthDate:        personForm.birthDate || null,
        birthCity:        personForm.birthCity.trim() || null,
        birthCountryCode: personForm.birthCountryCode || null,
        nationality:      personForm.nationality || null,
        photoUrl:         personForm.photoUrl.trim() || null,
        bio:              personForm.bio.trim() || null,
      });
      setSuccessMessage(t("admin_person_created"));
      closeModal();
      fetchPeople();
    } catch (err: any) {
      setModalError(err.message || t("admin_person_create_error"));
    }
  };

  // Payload of the person edit form — shared by the Save button and by the
  // "save before switching to a career modal" path below.
  const buildPersonUpdatePayload = () => ({
    firstName:        personForm.firstName.trim() || null,
    lastName:         personForm.lastName.trim() || null,
    knownName:        personForm.knownName,   // empty string = clear; null = no change
    birthDate:        personForm.birthDate || null,
    birthCity:        personForm.birthCity.trim() || null,
    birthCountryCode: personForm.birthCountryCode || null,
    nationality:      personForm.nationality || null,
    photoUrl:         personForm.photoUrl.trim() || null,
    bio:              personForm.bio.trim() || null,
    active:           personForm.active,
  });

  const handleUpdatePerson = async () => {
    if (!selectedPerson) return;
    try {
      await apiClient.put(`/api/admin/people/${selectedPerson.id}`, buildPersonUpdatePayload());
      setSuccessMessage(t("admin_person_updated"));
      closeModal();
      fetchPeople();
    } catch (err: any) {
      setModalError(err.message || t("admin_person_update_error"));
    }
  };

  /**
   * Persist the open person edit form before the modal is swapped for a career modal.
   * Without this, everything typed into the person form (birth place, birth date, bio, ...)
   * was thrown away as soon as the admin touched a career entry, because closeModal()
   * simply dropped the unsaved state. Returns false when the save failed, so the caller
   * can keep the person modal open and show the error.
   */
  const persistPersonEdits = async (): Promise<boolean> => {
    if (!selectedPerson) return false;
    try {
      await apiClient.put(`/api/admin/people/${selectedPerson.id}`, buildPersonUpdatePayload());
      return true;
    } catch (err: any) {
      setModalError(err.message || t("admin_person_update_error"));
      return false;
    }
  };

  const handleDeletePerson = async () => {
    if (!selectedPerson) return;
    try {
      await apiClient.delete(`/api/admin/people/${selectedPerson.id}`);
      setSuccessMessage(t("admin_person_deleted"));
      closeModal();
      fetchPeople();
    } catch (err: any) {
      setModalError(err.message || t("admin_person_delete_error"));
    }
  };

  const handleToggleActive = async (person: PersonModel) => {
    try {
      await apiClient.put(`/api/admin/people/${person.id}`, { active: !person.active });
      setSuccessMessage(
        !person.active
          ? t("admin_toggle_activated",   { name: person.fullName })
          : t("admin_toggle_deactivated", { name: person.fullName })
      );
      fetchPeople();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  // ── CRUD: Career entry ────────────────────────────────────────
  // Build payload from careerForm + club state.
  // If a club was selected from the dropdown, use its ID.
  // Otherwise, treat whatever is in clubQuery as a free-text customClubName.
  // ORGANIZATION and NONE roles always store free text (a TV channel or a federation is
  // never a club record); CLUB roles use the picked club when there is one and fall back
  // to free text when the club is not seeded in the database yet.
  const buildCareerPayload = () => {
    const typed = clubQuery.trim();
    const useClubId = needsClubSearch && !!selectedClub;
    return {
      clubId:         useClubId ? selectedClub!.id : null,
      customClubName: !useClubId && typed ? typed : null,
      role:           careerForm.role,
      sportType:      careerForm.sportType,
      startYear:      Number(careerForm.startYear),
      // Empty end year = still ongoing
      endYear:        careerForm.endYear !== "" ? Number(careerForm.endYear) : null,
      notes:          careerForm.notes.trim() || null,
    };
  };

  /**
   * Client-side mirror of the server rule: only roles with an affiliation need a value,
   * and organization roles need the free-text name rather than a club record.
   */
  const validateAffiliation = (): boolean => {
    if (!affiliationRequired) return true;
    if (selectedClub || clubQuery.trim()) return true;
    setModalError(needsClubSearch
      ? t("admin_career_club_required")
      : t("admin_career_organization_required"));
    return false;
  };

  const handleAddCareerEntry = async () => {
    if (!selectedPerson) return;
    if (!validateAffiliation()) return;
    try {
      await apiClient.post(`/api/admin/people/${selectedPerson.id}/career`, buildCareerPayload());
      setSuccessMessage(t("admin_career_added"));
      closeModal();
      fetchPeople();
    } catch (err: any) {
      setModalError(err.message || t("admin_career_add_error"));
    }
  };

  const handleUpdateCareerEntry = async () => {
    if (!selectedPerson || !selectedEntry) return;
    if (!validateAffiliation()) return;
    try {
      await apiClient.put(
        `/api/admin/people/${selectedPerson.id}/career/${selectedEntry.id}`,
        buildCareerPayload()
      );
      setSuccessMessage(t("admin_career_updated"));
      closeModal();
      fetchPeople();
    } catch (err: any) {
      setModalError(err.message || t("admin_career_update_error"));
    }
  };

  const handleDeleteCareerEntry = async () => {
    if (!selectedPerson || !selectedEntry) return;
    try {
      await apiClient.delete(`/api/admin/people/${selectedPerson.id}/career/${selectedEntry.id}`);
      setSuccessMessage(t("admin_career_deleted"));
      closeModal();
      fetchPeople();
    } catch (err: any) {
      setModalError(err.message || t("admin_career_delete_error"));
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="admin-people">

      <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_person_search_placeholder")}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}><FaSearch /></button>
        </div>

        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {t("admin_filters")}
        </button>

        {hasActiveFilters && (
          <button className="clear-filters" onClick={handleClearFilters}>
            <FaTimes /> {t("admin_clear_filters")}
          </button>
        )}

        <button className="create-person-btn" onClick={openCreateModal}>
          <FaPlus /> {t("admin_person_add")}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          {/* Birth country filter — typeable dropdown */}
          <div className="admin-filter-group">
            <label>{t("admin_person_col_birth_country")}</label>
            <SearchableSelect
              value={filters.birthCountry}
              options={countryOptions}
              placeholder={t("admin_all")}
              onChange={v => { setFilters(p => ({ ...p, birthCountry: v })); setCurrentPage(0); }}
            />
          </div>

          {/* Nationality filter — typeable dropdown */}
          <div className="admin-filter-group">
            <label>{t("admin_person_nationality")}</label>
            <SearchableSelect
              value={filters.nationality}
              options={countryOptions}
              placeholder={t("admin_all")}
              onChange={v => { setFilters(p => ({ ...p, nationality: v })); setCurrentPage(0); }}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_status_label")}</label>
            <select
              value={filters.active}
              onChange={e => { setFilters(p => ({ ...p, active: e.target.value as AdminPersonFilters["active"] })); setCurrentPage(0); }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_active")}</option>
              <option value="false">{t("admin_inactive")}</option>
            </select>
          </div>

          {/* Current-club filter — typeable dropdown over the full club list */}
          <div className="admin-filter-group">
            <label>{t("admin_person_filter_current_club")}</label>
            <SearchableSelect
              value={filters.currentClubId === "" ? "" : String(filters.currentClubId)}
              options={clubOptions}
              placeholder={t("admin_all")}
              onChange={v => {
                setFilters(p => ({ ...p, currentClubId: v === "" ? "" : Number(v) }));
                setCurrentPage(0);
              }}
            />
          </div>

          {/* Career-club filter — matches any stint in the person's career history */}
          <div className="admin-filter-group">
            <label>{t("admin_person_filter_career_club")}</label>
            <SearchableSelect
              value={filters.clubId === "" ? "" : String(filters.clubId)}
              options={clubOptions}
              placeholder={t("admin_all")}
              onChange={v => {
                setFilters(p => ({ ...p, clubId: v === "" ? "" : Number(v) }));
                setCurrentPage(0);
              }}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_ct_lock_status")}</label>
            <select
              value={filters.locked}
              onChange={e => { setFilters(p => ({ ...p, locked: e.target.value as AdminPersonFilters["locked"] })); setCurrentPage(0); }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_ct_locked")}</option>
              <option value="false">{t("admin_ct_unlocked")}</option>
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="people-table-container">
        {loading ? (
          <div className="people-loading">{t("loading")}</div>
        ) : error && people.length === 0 ? (
          <AdminErrorBanner message={error} />
        ) : people.length === 0 ? (
          <div className="people-empty">{t("people_not_found")}</div>
        ) : (
          <table className="people-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")}>ID {getSortIcon("id")}</th>
                <th onClick={() => handleSort("photoUrl")}>
                  {t("admin_person_col_avatar")} {getSortIcon("photoUrl")}
                </th>
                <th onClick={() => handleSort("lastName")}>
                  {t("admin_person_col_name")} {getSortIcon("lastName")}
                </th>
                <th onClick={() => handleSort("knownName")}>
                  {t("admin_person_col_known_name")} {getSortIcon("knownName")}
                </th>
                {/* Birth place — flag of the birth country */}
                <th onClick={() => handleSort("birthCountryCode")}>
                  {t("admin_person_col_birth_country")} {getSortIcon("birthCountryCode")}
                </th>
                <th onClick={() => handleSort("nationality")}>
                  {t("admin_person_nationality")} {getSortIcon("nationality")}
                </th>
                {/* Birth date shown as dd.MM.yyyy */}
                <th onClick={() => handleSort("birthDate")}>
                  {t("admin_person_col_birth_date")} {getSortIcon("birthDate")}
                </th>
                {/* Last career step — most recent club / organization.
                    Not sortable: the value is derived from the career list on the
                    client, while sorting is applied server-side before paging. */}
                <th className="col-last-career col-no-sort">
                  {t("admin_person_col_last_career", "Son Durum")}
                </th>
                <th onClick={() => handleSort("careerCount")}>
                  {t("admin_person_col_career")} {getSortIcon("careerCount")}
                </th>
                {/* entry count column — uses the same key as all other panels */}
                <th className="col-entries" onClick={() => handleSort("entryCount")} style={{ cursor: "pointer" }}>
                  {t("admin_col_entries")} {getSortIcon("entryCount")}
                </th>
                <th onClick={() => handleSort("active")}>
                  {t("admin_status_label")} {getSortIcon("active")}
                </th>
                <th>{t("admin_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {people.map(person => {
                // Resolved once per row and reused by the "Son Durum" column below
                const latestCareer = getLatestCareerEntry(person.careerEntries);

                return (
                <tr key={person.id} className={!person.active ? "row-inactive" : ""}>
                  <td>#{person.id}</td>

                  <td>
                    {person.photoUrl ? (
                      <img
                        className="person-avatar-thumb"
                        src={`${API_BASE}${person.photoUrl}`}
                        alt={person.fullName}
                        onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="person-avatar-initials">
                        {getInitials(person.firstName, person.lastName)}
                      </div>
                    )}
                  </td>

                  <td className="person-fullname">
                    <Link
                      to={`/kisi/${person.slug || person.id}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                    >
                      {person.fullName}
                    </Link>
                  </td>

                  {/* knownName (short/popular name) column */}
                  <td>
                    {person.knownName
                      ? <span className="person-known-name-badge">{person.knownName}</span>
                      : <span className="text-muted-cell">—</span>
                    }
                  </td>

                  {/* Birth country flag column */}
                  <td>
                    {person.birthCountryCode ? (
                      <span className="person-nat-badge">
                        <img
                          className="person-flag-thumb"
                          src={flagUrl(person.birthCountryCode) ?? ""}
                          alt={person.birthCountryCode}
                          title={person.birthCity
                            ? `${person.birthCity} (${person.birthCountryCode})`
                            : person.birthCountryCode}
                          onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                        {person.birthCountryCode}
                      </span>
                    ) : (
                      <span className="text-muted-cell">—</span>
                    )}
                  </td>

                  {/* Nationality flag column */}
                  <td>
                    {person.nationality ? (
                      <span className="person-nat-badge">
                        <img
                          className="person-flag-thumb"
                          src={flagUrl(person.nationality) ?? ""}
                          alt={person.nationality}
                          title={person.nationality}
                          onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                        {person.nationality}
                      </span>
                    ) : (
                      <span className="text-muted-cell">—</span>
                    )}
                  </td>

                  {/* Birth date column — dd.MM.yyyy */}
                  <td className="person-birth-date">
                    {formatBirthDate(person.birthDate) ?? <span className="text-muted-cell">—</span>}
                  </td>

                  {/* Last career step — club logo (when the stint is linked to a real
                      club record) plus the role and the years of that stint. */}
                  <td className="person-last-career">
                    {latestCareer ? (
                      <div
                        className="last-career-cell"
                        title={`${careerAffiliationLabel(latestCareer, translateRole(latestCareer.role))} · ${translateRole(latestCareer.role)} · ${formatYears(latestCareer.startYear, latestCareer.endYear)}`}
                      >
                        <span className="last-career-cell__main">
                          {latestCareer.clubLogoUrl && (
                            <img
                              className="last-career-logo"
                              src={`${API_BASE}${latestCareer.clubLogoUrl}`}
                              alt=""
                              onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          )}
                          <span className="last-career-name">
                            {careerAffiliationLabel(latestCareer, translateRole(latestCareer.role))}
                          </span>
                          {/* Ongoing stints are marked so a finished one is never
                              mistaken for the person's current situation. */}
                          {latestCareer.endYear == null && (
                            <span className="last-career-live" title={t("admin_career_ongoing")} />
                          )}
                        </span>
                        <span className="last-career-cell__sub">
                          {translateRole(latestCareer.role)} · {formatYears(latestCareer.startYear, latestCareer.endYear)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-cell">—</span>
                    )}
                  </td>

                  <td>
                    <button
                      className="action-btn"
                      title={t("admin_person_manage_career")}
                      onClick={() => openCareerAddModal(person)}
                      style={{ width: "auto", padding: "3px 8px", gap: 5, fontSize: 11 }}
                    >
                      <FaPlus size={9} />
                      <span className="person-career-count">{person.careerEntries?.length ?? 0}</span>
                    </button>
                  </td>

                  <td className="person-entry-count">{person.entryCount ?? 0}</td>

                  <td>
                    <button
                      className={`active-toggle-btn ${person.active ? "is-active" : "is-inactive"}`}
                      onClick={() => handleToggleActive(person)}
                    >
                      {person.active
                        ? <><FaToggleOn /> {t("admin_active")}</>
                        : <><FaToggleOff /> {t("admin_inactive")}</>}
                    </button>
                  </td>

                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit" title={t("admin_edit_btn")} onClick={() => openEditModal(person)}>
                        <FaEdit />
                      </button>
                      <button
                        className={person.isLocked ? "action-btn lock-active" : "action-btn lock"}
                        title={person.isLocked ? t("admin_topic_unlock") : t("admin_topic_lock")}
                        onClick={() => handleToggleLock(person)}
                      >
                        {person.isLocked ? <FaUnlock size={12} /> : <FaLock size={12} />}
                      </button>
                      {person.isLocked && (
                        <button
                          className="action-btn"
                          title={t("admin_lock_info_title")}
                          onClick={() => setLockInfoTarget(person)}
                        >
                          <FaInfoCircle size={12} />
                        </button>
                      )}
                      {isAdmin && (
                        <button className="action-btn danger" title={t("admin_delete_btn")} onClick={() => openDeleteModal(person)}>
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="people-pagination">
          <span className="page-info">
            {t("admin_page_info", { current: currentPage + 1, total: totalPages, items: totalItems })}
          </span>
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            paginate={handlePaginate}
          />
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Create / Edit Person Modal */}
      {(modalType === "create" || modalType === "edit") && (
        <div className="admin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal person-form-modal">
            <div className="admin-modal-header">
              <h3>
                {modalType === "create"
                  ? t("admin_person_create_title")
                  : t("admin_person_edit_title")}
              </h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_person_first_name")} *</label>
                  <input type="text" value={personForm.firstName}
                    maxLength={ADMIN_PERSON_NAME_MAX_LENGTH}
                    onChange={e => setPersonForm(p => ({ ...p, firstName: e.target.value }))} />
                  <AdminCharCounter value={personForm.firstName} max={ADMIN_PERSON_NAME_MAX_LENGTH} />
                </div>
                <div className="form-group">
                  <label>{t("admin_person_last_name")} *</label>
                  <input type="text" value={personForm.lastName}
                    maxLength={ADMIN_PERSON_NAME_MAX_LENGTH}
                    onChange={e => setPersonForm(p => ({ ...p, lastName: e.target.value }))} />
                  <AdminCharCounter value={personForm.lastName} max={ADMIN_PERSON_NAME_MAX_LENGTH} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--full">
                  {/* knownName: short/popular name used as primary display name and URL slug */}
                  <label>{t("admin_person_known_name")}</label>
                  <input
                    type="text"
                    placeholder={t("admin_person_known_name_placeholder")}
                    value={personForm.knownName}
                    maxLength={ADMIN_PERSON_NAME_MAX_LENGTH}
                    onChange={e => setPersonForm(p => ({ ...p, knownName: e.target.value }))}
                  />
                  {/* Hint and counter share one line so the field lines up with
                      the counter-only fields around it. */}
                  <div className="admin-field-footer">
                    <span className="form-hint">{t("admin_person_known_name_hint")}</span>
                    <AdminCharCounter value={personForm.knownName} max={ADMIN_PERSON_NAME_MAX_LENGTH} />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_person_birth_date")}</label>
                  <input type="date" value={personForm.birthDate}
                    onChange={e => setPersonForm(p => ({ ...p, birthDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t("admin_person_birth_city")}</label>
                  <input type="text" value={personForm.birthCity}
                    maxLength={ADMIN_PERSON_BIRTH_CITY_MAX_LENGTH}
                    onChange={e => setPersonForm(p => ({ ...p, birthCity: e.target.value }))} />
                  <AdminCharCounter value={personForm.birthCity} max={ADMIN_PERSON_BIRTH_CITY_MAX_LENGTH} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_person_birth_country")}</label>
                  <select value={personForm.birthCountryCode}
                    onChange={e => setPersonForm(p => ({ ...p, birthCountryCode: e.target.value }))}>
                    <option value="">—</option>
                    {countries.map(c => <option key={c.code} value={c.code}>{c.code} — {c.displayName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_person_nationality")}</label>
                  <select value={personForm.nationality}
                    onChange={e => setPersonForm(p => ({ ...p, nationality: e.target.value }))}>
                    <option value="">—</option>
                    {countries.map(c => <option key={c.code} value={c.code}>{c.code} — {c.displayName}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>{t("admin_person_photo_url")}</label>
                  <input type="text" value={personForm.photoUrl}
                    maxLength={ADMIN_URL_MAX_LENGTH}
                    onChange={e => setPersonForm(p => ({ ...p, photoUrl: e.target.value }))} />
                  <AdminCharCounter value={personForm.photoUrl} max={ADMIN_URL_MAX_LENGTH} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>{t("admin_person_bio")}</label>
                  <textarea value={personForm.bio}
                    onChange={e => setPersonForm(p => ({ ...p, bio: e.target.value }))} maxLength={ADMIN_PERSON_BIO_MAX_LENGTH} />
                  <AdminCharCounter value={personForm.bio} max={ADMIN_PERSON_BIO_MAX_LENGTH} />
                </div>
              </div>

              {/* Career entries panel — only visible in edit mode */}
              {modalType === "edit" && selectedPerson && (
                <CareerPanel
                  person={selectedPerson}
                  onAddEntry={async () => {
                    // Save the person form first — switching modals used to discard it
                    const person = selectedPerson;
                    if (!(await persistPersonEdits())) return;
                    setSuccessMessage(t("admin_person_updated"));
                    closeModal();
                    await openCareerAddModal(person);
                    fetchPeople();
                  }}
                  onEditEntry={async (entry) => {
                    const person = selectedPerson;
                    if (!(await persistPersonEdits())) return;
                    setSuccessMessage(t("admin_person_updated"));
                    closeModal();
                    openCareerEditModal(person, entry);
                    fetchPeople();
                  }}
                  onDeleteEntry={async (entry) => {
                    const person = selectedPerson;
                    if (!(await persistPersonEdits())) return;
                    setSuccessMessage(t("admin_person_updated"));
                    closeModal();
                    openCareerDeleteModal(person, entry);
                    fetchPeople();
                  }}
                  t={t}
                />
              )}
            </div>

            <div className="admin-modal-footer">
              <button className="btn-secondary" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="btn-primary" onClick={modalType === "create" ? handleCreatePerson : handleUpdatePerson}>
                {modalType === "create" ? t("admin_create") : t("admin_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Career Add / Edit Modal */}
      {(modalType === "career-add" || modalType === "career-edit") && selectedPerson && (
        <div className="admin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal career-form-modal">
            <div className="admin-modal-header">
              <h3>
                {modalType === "career-add"
                  ? t("admin_career_add_title")
                  : t("admin_career_edit_title")}
                <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 8, color: "var(--text-muted)" }}>
                  — {selectedPerson.knownName ?? selectedPerson.fullName}
                </span>
              </h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_career_role")} *</label>
                  <select value={careerForm.role}
                    onChange={e => {
                      const newRole = e.target.value;
                      const newAffiliation = getAffiliationType(newRole);
                      setCareerForm(p => ({ ...p, role: newRole }));
                      // ORGANIZATION and NONE roles are never club records — drop any picked
                      // club but keep whatever the admin already typed. Clearing the text here
                      // used to silently erase an organization name such as "TFF" the moment
                      // the role was switched to a referee.
                      if (newAffiliation !== "CLUB") {
                        setSelectedClub(null);
                        setClubResults([]);
                        setShowClubDrop(false);
                      }
                    }}>
                    {personRoles.map(r => (
                      <option key={r.code} value={r.code}>{translateRole(r.code)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_career_sport")} *</label>
                  <select value={careerForm.sportType}
                    onChange={e => setCareerForm(p => ({ ...p, sportType: e.target.value }))}>
                    {SPORT_TYPES.map(s => (
                      <option key={s} value={s}>{t(`menu_sport_${s.toLowerCase()}`, s)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Affiliation field — driven by the role's affiliation type:
                    NONE         : optional free-text employer (a referee working for TFF)
                    ORGANIZATION : required text input for a TV channel / newspaper / federation
                    CLUB         : club search; picking a result stores the club ID, while
                                   free text is kept as customClubName for clubs that are
                                   not in the database yet. */}
              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>
                    {needsClubSearch ? t("admin_career_club") : t("admin_career_organization")}
                    {affiliationRequired ? " *" : ""}
                  </label>
                  <div className="club-search-wrapper" ref={clubDropRef}>
                    <input
                      type="text"
                      className={`club-search-input ${selectedClub ? "club-input--selected" : ""}`}
                      placeholder={needsClubSearch
                        ? t("admin_career_club_search_placeholder")
                        : t("admin_career_organization_placeholder")}
                      value={clubQuery}
                      maxLength={ADMIN_CAREER_CLUB_NAME_MAX_LENGTH}
                      onChange={e => handleClubQueryChange(e.target.value)}
                      onFocus={() => needsClubSearch && clubResults.length > 0 && setShowClubDrop(true)}
                    />
                    {needsClubSearch && clubSearchLoading && <span className="club-search-spinner" />}
                    {/* Green checkmark when a known club is selected */}
                    {needsClubSearch && selectedClub && !clubSearchLoading && (
                      <span className="club-input-check">✓</span>
                    )}
                    {needsClubSearch && showClubDrop && clubResults.length > 0 && (
                      <ul className="club-search-dropdown">
                        {clubResults.map(club => (
                          <li key={club.id} onMouseDown={() => handleSelectClub(club)}>
                            {club.nameTr ?? club.shortName}
                            {club.country && <span className="club-search-country">{club.country}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* Hint and counter share one line so the field lines up with
                      the counter-only fields around it. */}
                  <div className="admin-field-footer">
                    <span className="form-hint">
                      {!needsClubSearch
                        ? t("admin_career_organization_hint")
                        : (!selectedClub && clubQuery.trim()
                            ? t("admin_career_club_freetext_hint")
                            : t("admin_career_club_hint", "Arama yapın ve seçin — veya listede yoksa direkt yazın."))}
                    </span>
                    <AdminCharCounter value={clubQuery} max={ADMIN_CAREER_CLUB_NAME_MAX_LENGTH} />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--sm">
                  <label>{t("admin_career_start_year")} *</label>
                  <input type="number" min={1900} max={2100}
                    value={careerForm.startYear}
                    onChange={e => setCareerForm(p => ({ ...p, startYear: Number(e.target.value) }))} />
                </div>
                <div className="form-group form-group--sm">
                  {/* An empty end year means the stint is still ongoing. The checkbox below
                      makes that explicit so an open-ended record can never be mistaken for
                      one that ended in the current year. */}
                  <label>{t("admin_career_end_year")}</label>
                  <input type="number" min={1900} max={2100}
                    placeholder={t("admin_career_ongoing_placeholder")}
                    disabled={careerOngoing}
                    value={careerForm.endYear}
                    onChange={e => setCareerForm(p => ({ ...p, endYear: e.target.value }))} />
                </div>
                <div className="form-group form-group--check">
                  <label className="career-ongoing-check">
                    <input
                      type="checkbox"
                      checked={careerOngoing}
                      onChange={e => setCareerForm(p => ({
                        ...p,
                        // Checking clears the year (ongoing); unchecking seeds the current year
                        endYear: e.target.checked ? "" : CURRENT_YEAR,
                      }))}
                    />
                    {t("admin_career_ongoing")}
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>{t("admin_career_notes")}</label>
                  <input type="text" maxLength={ADMIN_CAREER_NOTES_MAX_LENGTH} value={careerForm.notes}
                    onChange={e => setCareerForm(p => ({ ...p, notes: e.target.value }))} />
                  <AdminCharCounter value={careerForm.notes} max={ADMIN_CAREER_NOTES_MAX_LENGTH} />
                </div>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="btn-secondary" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="btn-primary"
                onClick={modalType === "career-add" ? handleAddCareerEntry : handleUpdateCareerEntry}>
                {modalType === "career-add" ? t("admin_add") : t("admin_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Person Modal */}
      {modalType === "delete" && selectedPerson && (
        <div className="admin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal delete-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_person_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p>{t("admin_person_delete_confirm", { name: selectedPerson.fullName })}</p>
              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {t("admin_person_delete_warning")}
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className="btn-secondary" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="btn-danger" onClick={handleDeletePerson}>{t("admin_delete_btn")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Career Entry Modal */}
      {modalType === "career-delete" && selectedPerson && selectedEntry && (
        <div className="admin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal delete-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_career_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p>
                <strong>{selectedPerson.knownName ?? selectedPerson.fullName}</strong> —{" "}
                {translateRole(selectedEntry.role)}{" "}
                @ {selectedEntry.clubShortName ?? selectedEntry.customClubName ?? "—"}{" "}
                ({formatYears(selectedEntry.startYear, selectedEntry.endYear)})
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className="btn-secondary" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="btn-danger" onClick={handleDeleteCareerEntry}>{t("admin_delete_btn")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Lock-with-reason modal — only shown when locking, unlock stays one-click */}
      {lockTarget && (
        <ConfirmModal
          message={t("admin_person_lock_confirm")}
          confirmLabel={lockBusy ? "..." : t("admin_topic_lock")}
          cancelLabel={t("admin_cancel")}
          onConfirm={handleConfirmLock}
          onCancel={handleCancelLock}
          disabled={lockBusy}
        >
          <LockReasonFields
            reason={lockReasonDraft}
            onReasonChange={setLockReasonDraft}
            visible={lockReasonVisibleDraft}
            onVisibleChange={setLockReasonVisibleDraft}
          />
        </ConfirmModal>
      )}

      {lockInfoTarget && (
        <LockInfoModal
          lockedByUsername={lockInfoTarget.lockedByUsername}
          lockedAt={lockInfoTarget.lockedAt}
          lockReason={lockInfoTarget.lockReason}
          lockReasonVisible={lockInfoTarget.lockReasonVisible}
          onClose={() => setLockInfoTarget(null)}
        />
      )}
    </div>
  );
};

// ── Career panel sub-component (inside edit modal) ─────────────────────────

interface CareerPanelProps {
  person: PersonModel;
  onAddEntry: () => void;
  onEditEntry: (entry: CareerEntryModel) => void;
  onDeleteEntry: (entry: CareerEntryModel) => void;
  t: (key: string, opts?: any) => string;
}

const CareerPanel: React.FC<CareerPanelProps> = ({ person, onAddEntry, onEditEntry, onDeleteEntry, t }) => {
  const { translateRole } = usePersonRoles();
  const entries = person.careerEntries ?? [];

  return (
    <div className="career-panel">
      <p className="career-panel__title">{t("person_career_history", "Kariyer")} ({entries.length})</p>

      <div className="career-panel__list">
        {entries.map(entry => (
          <div key={entry.id} className="career-panel__entry">
            <div className="career-panel__entry-info">
              <div className="career-panel__entry-main">
                {/* Entries with no club and no organization show the role instead of a dash */}
                {entry.clubShortName ?? entry.customClubName ?? translateRole(entry.role)}
              </div>
              <div className="career-panel__entry-sub">
                {translateRole(entry.role)} ·{" "}
                {t(`menu_sport_${entry.sportType.toLowerCase()}`, entry.sportType)} ·{" "}
                {formatYears(entry.startYear, entry.endYear)}
              </div>
            </div>
            <div className="career-panel__entry-actions">
              <button className="action-btn" title={t("admin_edit_btn")} onClick={() => onEditEntry(entry)}>
                <FaEdit size={11} />
              </button>
              <button className="action-btn danger" title={t("admin_delete_btn")} onClick={() => onDeleteEntry(entry)}>
                <FaTrash size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button className="career-panel__add-btn" onClick={onAddEntry}>
        <FaPlus size={10} /> {t("admin_career_add_entry")}
      </button>
    </div>
  );
};